import { useCallback, useEffect, useState, lazy, Suspense, useMemo } from "react";
import axios from "../api/axiosInstance";
import { Link, useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import useBlogStore from "../store/useblogStore";

const BlogCard = lazy(() => import("../Components/Blogcard"));
const PAGE_SIZE = 6;
const categoriesList = ["All", "Case Study", "Tech Journey", "Project Deep Dive", "General Tech"];

const Blog = () => {
  const [query, setQuery] = useState("");
  const [localResults, setLocalResults] = useState([]);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreLocal, setHasMoreLocal] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");

  // Zustand selectors
  const blogs = useBlogStore((s) => s.blogs);
  const setBlogs = useBlogStore((s) => s.setBlogs);
  const appendBlogs = useBlogStore((s) => s.appendBlogs);
  const clearBlogs = useBlogStore((s) => s.clearBlogs);

  // fetch helper (page-based)
  const fetchBlogs = useCallback(async (pageNumber = 1, searchQuery = "") => {
    try {
      setLoadingLocal(true);
      let url = `/blog?page=${pageNumber}&limit=${PAGE_SIZE}`;
      if (tag) url += `&tag=${encodeURIComponent(tag)}`;
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await axios.get(url);
      const data = res.data || [];

      if (pageNumber === 1) {
        setBlogs(data);
      } else {
        appendBlogs(data);
      }

      if (data.length < PAGE_SIZE) setHasMoreLocal(false);
      else setHasMoreLocal(true);
    } catch (err) {
      setError(err.message || "Failed to fetch blogs");
    } finally {
      setLoadingLocal(false);
    }
  }, [appendBlogs, setBlogs, tag, selectedCategory]);

  // reset on tag or category change
  useEffect(() => {
    clearBlogs();
    setPage(1);
    setHasMoreLocal(true);
    fetchBlogs(1);
  }, [clearBlogs, fetchBlogs, tag, selectedCategory]);

  // pagination
  useEffect(() => {
    if (page === 1) return;
    fetchBlogs(page);
  }, [fetchBlogs, page]);

  // search logic (local + server)
  const handleSearchInput = (value) => {
    setQuery(value);
    const q = value.toLowerCase();
    const filtered = blogs.filter((b) => 
      ((b.title || "") + (b.content || "")).toLowerCase().includes(q)
    );
    setLocalResults(filtered);

    if (filtered.length < 3 && q.length > 2) {
      debouncedServerSearch(value);
    }
  };

  const debouncedServerSearch = useMemo(
    () =>
      debounce((val) => {
        fetchBlogs(1, val);
      }, 600),
    [fetchBlogs]
  );

  useEffect(() => {
    return () => debouncedServerSearch.cancel();
  }, [debouncedServerSearch]);

  const onEnter = (e) => {
    if (e.key === "Enter") {
      fetchBlogs(1, query);
    }
  };

  const results = query ? localResults : blogs;

  return (
    <div className="min-h-screen mt-5 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Title + Action */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl mb-2">Latest Writings</h2>
            <p className="text-gray-400 text-sm">Explaining projects, technical learning journeys, and case studies.</p>
          </div>
          <Link to="/create" className="inline-flex items-center justify-center rounded-xl bg-cyan-600 px-6 py-3 text-base font-medium text-white hover:bg-cyan-700 transition">
            Write Article
          </Link>
        </div>

        {/* Search Input */}
        <div className="mb-8">
          <input
            type="text"
            placeholder="Search blogs..."
            value={query}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={onEnter}
            className="w-full sm:w-96 px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-cyan-500 text-sm"
          />
        </div>

        {/* Category Pills Filter */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/5 pb-6">
          {categoriesList.map((cat) => {
            const isSelected = (cat === "All" && !selectedCategory) || (selectedCategory === cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat === "All" ? "" : cat)}
                className={`px-4 py-1.5 rounded-xl text-xs font-semibold border transition ${
                  isSelected
                    ? "bg-cyan-600/30 text-cyan-300 border-cyan-500/60"
                    : "bg-slate-900 text-gray-400 border-slate-800 hover:text-white hover:border-slate-700"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Blogs Feed List */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
          {results.slice().sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).map(b => (
            <Suspense fallback={<BlogSkeleton />} key={b._id}>
              <BlogCard blog={b} />
            </Suspense>
          ))}

          {(loadingLocal || (results.length === 0 && !query && blogs.length === 0)) &&
            Array.from({ length: 3 }).map((_, i) => <BlogSkeleton key={i} />)}
            
          {!loadingLocal && results.length === 0 && (
            <p className="text-gray-400 text-center py-10 italic">No posts found matching the criteria.</p>
          )}
        </div>

        {!loadingLocal && hasMoreLocal && results.length >= PAGE_SIZE && (
          <div className="flex justify-center mt-8">
            <button className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition" onClick={() => setPage(p => p + 1)}>
              Load More
            </button>
          </div>
        )}

        {error && <p className="text-red-400 text-center mt-8">Error: {error}</p>}
      </div>
    </div>
  );
};

const BlogSkeleton = () => (
  <div className="animate-pulse bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
    <div className="h-6 bg-slate-800 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-slate-700 rounded w-full mb-2"></div>
    <div className="h-4 bg-slate-700 rounded w-5/6 mb-4"></div>
    <div className="h-3 bg-slate-600 rounded w-1/3"></div>
  </div>
);

export default Blog;
